// src/tattoo/components/JoinTattuneArtistWizard.jsx
import React, { useState } from "react";
import { upsertOwnShopProfile } from "../lib/supabaseClient";

const ALL_STYLES = [
  "Traditional",
  "Neo-traditional",
  "Black & Grey",
  "Fine line",
  "Japanese",
  "Realism",
  "Illustrative",
  "Lettering",
  "Geometric",
  "Ornamental",
];

export default function JoinTattuneArtistWizard({ onClose }) {
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(false);

  const [form, setForm] = useState({
    name: "",
    displayName: "",
    email: "",
    city: "",
    region: "",
    country: "",
    tagline: "",
    bio: "",
    instagram: "",
    website: "",
    styles: [],
    acceptPoolRequests: true,
    bookingLink: "",
    minRate: "",
  });

  function updateField(key, value) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  function toggleStyle(style) {
    setForm((f) => {
      const has = f.styles.includes(style);
      return {
        ...f,
        styles: has
          ? f.styles.filter((s) => s !== style)
          : [...f.styles, style],
      };
    });
  }

  async function handleSubmit() {
    setSaving(true);
    setError(null);
    try {
      const payload = {
        name: form.name.trim(),
        displayName: form.displayName.trim() || form.name.trim(),
        email: form.email.trim(),
        city: form.city.trim() || null,
        region: form.region.trim() || null,
        country: form.country.trim() || null,
        tagline: form.tagline.trim() || null,
        bio: form.bio.trim() || null,
        instagram: form.instagram.trim() || null,
        website: form.website.trim() || null,
        styles: form.styles,
        acceptPoolRequests: !!form.acceptPoolRequests,
        bookingLink: form.bookingLink.trim() || null,
        minRate: form.minRate ? Number(form.minRate) : null,
      };

      await upsertOwnShopProfile(payload);
      setDone(true);
    } catch (err) {
      console.error("[tattunity] wizard submit error", err);
      setError(err.message || "Could not save your studio profile.");
    } finally {
      setSaving(false);
    }
  }

  const canNextFrom1 =
    form.name.trim() && form.email.trim() && form.city.trim();
  const canNextFrom2 = form.styles.length > 0;

  return (
    <div
      className="tt-join-overlay"
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 9999,
        background: "rgba(0,0,0,0.65)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 16,
      }}
      onClick={onClose}
    >
      <div
        className="tt-join-card"
        style={{
          maxWidth: 520,
          width: "50%",
          maxHeight: "90vh",
          overflow: "auto",
          background: "#111",
          borderRadius: 18,
          border: "1px solid #333",
          padding: 20,
          color: "#f5f5f5",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* header */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 12,
            gap: 12,
          }}
        >
          <div>
            <div
              style={{
                fontSize: 14,
                textTransform: "uppercase",
                letterSpacing: 0.18,
                opacity: 0.8,
              }}
            >
              Become a Tattune artist
            </div>
            <h2 style={{ margin: 0, fontSize: 20 }}>
              Studio profile setup
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            style={{
              borderRadius: 999,
              width: 28,
              height: 28,
              border: "1px solid #444",
              background: "transparent",
              color: "#aaa",
              cursor: "pointer",
            }}
          >
            ✕
          </button>
        </div>

        {/* step indicator */}
        <div
          style={{
            display: "flex",
            gap: 6,
            fontSize: 11,
            marginBottom: 12,
            opacity: 0.8,
          }}
        >
          <span
            style={{
              fontWeight: step === 1 ? 700 : 400,
            }}
          >
            1. Basics
          </span>
          <span>·</span>
          <span
            style={{
              fontWeight: step === 2 ? 700 : 400,
            }}
          >
            2. Styles
          </span>
          <span>·</span>
          <span
            style={{
              fontWeight: step === 3 ? 700 : 400,
            }}
          >
            3. Details
          </span>
        </div>

        {/* DONE view */}
        {done ? (
          <div>
            <p style={{ marginBottom: 12 }}>
              Your studio profile is saved and visible in the directory.
            </p>
            <p style={{ fontSize: 13, opacity: 0.8 }}>
              You can update it any time from this device, and clients can now
              discover you through Tattune&apos;s artist directory and quote
              requests.
            </p>
            <button
              type="button"
              onClick={onClose}
              style={{
                marginTop: 16,
                padding: "10px 18px",
                borderRadius: 999,
                border: "none",
                background: "#a2332b",
                color: "#fff",
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              Close
            </button>
          </div>
        ) : (
          <>
            {/* STEP 1: basics */}
            {step === 1 && (
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                <label style={{ fontSize: 12 }}>
                  Studio name
                  <input
                    className="tt-lead-input"
                    style={{ marginTop: 4 }}
                    value={form.name}
                    onChange={(e) => updateField("name", e.target.value)}
                    placeholder="Dark Tides Tattoo"
                  />
                </label>

                <label style={{ fontSize: 12 }}>
                  Public display name (optional)
                  <input
                    className="tt-lead-input"
                    style={{ marginTop: 4 }}
                    value={form.displayName}
                    onChange={(e) =>
                      updateField("displayName", e.target.value)
                    }
                    placeholder="Dark Tides · Kingston"
                  />
                </label>

                <label style={{ fontSize: 12 }}>
                  Contact email for leads
                  <input
                    className="tt-lead-input"
                    style={{ marginTop: 4 }}
                    type="email"
                    value={form.email}
                    onChange={(e) => updateField("email", e.target.value)}
                    placeholder="you@studio.com"
                  />
                </label>

                <div
                  style={{
                    display: "flex",
                    gap: 8,
                    marginTop: 4,
                    flexWrap: "wrap",
                  }}
                >
                  <input
                    className="tt-lead-input"
                    style={{ flex: 1, minWidth: 120 }}
                    value={form.city}
                    onChange={(e) => updateField("city", e.target.value)}
                    placeholder="City"
                  />
                  <input
                    className="tt-lead-input"
                    style={{ width: 80 }}
                    value={form.region}
                    onChange={(e) => updateField("region", e.target.value)}
                    placeholder="State/Prov"
                  />
                  <input
                    className="tt-lead-input"
                    style={{ width: 120 }}
                    value={form.country}
                    onChange={(e) => updateField("country", e.target.value)}
                    placeholder="Country"
                  />
                </div>

                <label style={{ fontSize: 12, marginTop: 4 }}>
                  Tagline (short)
                  <input
                    className="tt-lead-input"
                    style={{ marginTop: 4 }}
                    value={form.tagline}
                    onChange={(e) => updateField("tagline", e.target.value)}
                    placeholder="Award-winning custom work in Kingston."
                  />
                </label>
              </div>
            )}

            {/* STEP 2: styles */}
            {step === 2 && (
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                <p style={{ fontSize: 13, opacity: 0.85 }}>
                  Pick the styles you&apos;re happy to be discovered for:
                </p>
                <div
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 6,
                  }}
                >
                  {ALL_STYLES.map((style) => {
                    const on = form.styles.includes(style);
                    return (
                      <button
                        key={style}
                        type="button"
                        onClick={() => toggleStyle(style)}
                        style={{
                          borderRadius: 999,
                          padding: "6px 10px",
                          border: on
                            ? "1px solid #f5f5f5"
                            : "1px solid #444",
                          background: on ? "#f5f5f5" : "transparent",
                          color: on ? "#111" : "#ddd",
                          fontSize: 12,
                          cursor: "pointer",
                        }}
                      >
                        {style}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* STEP 3: details */}
            {step === 3 && (
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                <label style={{ fontSize: 12 }}>
                  Instagram
                  <input
                    className="tt-lead-input"
                    style={{ marginTop: 4 }}
                    value={form.instagram}
                    onChange={(e) => updateField("instagram", e.target.value)}
                    placeholder="https://instagram.com/yourstudio"
                  />
                </label>

                <label style={{ fontSize: 12 }}>
                  Website / booking link
                  <input
                    className="tt-lead-input"
                    style={{ marginTop: 4 }}
                    value={form.website}
                    onChange={(e) => updateField("website", e.target.value)}
                    placeholder="https://yourstudio.com"
                  />
                </label>

                <label style={{ fontSize: 12 }}>
                  Minimum rate (optional)
                  <input
                    className="tt-lead-input"
                    style={{ marginTop: 4 }}
                    type="number"
                    min="0"
                    value={form.minRate}
                    onChange={(e) => updateField("minRate", e.target.value)}
                    placeholder="e.g. 150"
                  />
                </label>

                <label style={{ fontSize: 12 }}>
                  Studio bio
                  <textarea
                    className="tt-lead-textarea"
                    style={{ marginTop: 4, minHeight: 80 }}
                    value={form.bio}
                    onChange={(e) => updateField("bio", e.target.value)}
                    placeholder="Tell clients how you work, what you love to tattoo, and anything they should know before booking."
                  />
                </label>

                <label
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    fontSize: 13,
                    marginTop: 4,
                  }}
                >
                  <input
                    type="checkbox"
                    checked={form.acceptPoolRequests}
                    onChange={(e) =>
                      updateField("acceptPoolRequests", e.target.checked)
                    }
                  />
                  <span>Send me relevant quote requests from Tattune artists</span>
                </label>
              </div>
            )}

            {/* error */}
            {error && (
              <p
                style={{
                  color: "#ff6b6b",
                  fontSize: 12,
                  marginTop: 8,
                }}
              >
                {error}
              </p>
            )}

            {/* footer buttons */}
            <div
              style={{
                marginTop: 16,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 8,
              }}
            >
              <button
                type="button"
                onClick={onClose}
                disabled={saving}
                style={{
                  borderRadius: 999,
                  padding: "8px 12px",
                  border: "1px solid #444",
                  background: "transparent",
                  color: "#ddd",
                  fontSize: 13,
                  cursor: "pointer",
                }}
              >
                Cancel
              </button>

              <div style={{ display: "flex", gap: 8 }}>
                {step > 1 && (
                  <button
                    type="button"
                    onClick={() => setStep((s) => s - 1)}
                    disabled={saving}
                    style={{
                      borderRadius: 999,
                      padding: "8px 14px",
                      border: "1px solid #444",
                      background: "transparent",
                      color: "#ddd",
                      fontSize: 13,
                      cursor: "pointer",
                    }}
                  >
                    Back
                  </button>
                )}

                {step < 3 && (
                  <button
                    type="button"
                    onClick={() => {
                      if (step === 1 && !canNextFrom1) return;
                      if (step === 2 && !canNextFrom2) return;
                      setStep((s) => s + 1);
                    }}
                    disabled={
                      saving ||
                      (step === 1 && !canNextFrom1) ||
                      (step === 2 && !canNextFrom2)
                    }
                    style={{
                      borderRadius: 999,
                      padding: "8px 16px",
                      border: "none",
                      background: "#a2332b",
                      color: "#fff",
                      fontWeight: 600,
                      fontSize: 13,
                      cursor: "pointer",
                      opacity:
                        (step === 1 && !canNextFrom1) ||
                        (step === 2 && !canNextFrom2)
                          ? 0.5
                          : 1,
                    }}
                  >
                    Next →
                  </button>
                )}

                {step === 3 && (
                  <button
                    type="button"
                    onClick={handleSubmit}
                    disabled={saving}
                    style={{
                      borderRadius: 999,
                      padding: "8px 16px",
                      border: "none",
                      background: "#a2332b",
                      color: "#fff",
                      fontWeight: 600,
                      fontSize: 13,
                      cursor: "pointer",
                      opacity: saving ? 0.7 : 1,
                    }}
                  >
                    {saving ? "Saving…" : "Create profile"}
                  </button>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}